import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { createUser, getUser } from '../services/userService';
import { Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import UserAgreementModal from '../components/UserAgreementModal';
import RecaptchaComponent from '../components/RecaptchaComponent';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';

const Signup = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState('');
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword || !formData.displayName) {
      setError(t('errors.allFieldsRequired'));
      return false;
    }

    if (formData.password.length < 6) {
      setError(t('errors.passwordTooShort'));
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('errors.passwordsDoNotMatch'));
      return false;
    }

    if (!agreementAccepted) {
      setError(t('errors.mustAcceptAgreement'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Execute reCAPTCHA verification
      if (recaptchaRef.current && recaptchaRef.current.executeRecaptcha) {
        const token = await recaptchaRef.current.executeRecaptcha();
        if (!token) {
          setError(t('errors.recaptchaVerificationFailed'));
          setLoading(false);
          return;
        }
      }

      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );

      const user = userCredential.user;

      // Update display name
      await updateProfile(user, {
        displayName: formData.displayName
      });

      // Create user document in Firestore
      await createUser(user.uid, formData.email, {
        displayName: formData.displayName,
        emailVerified: false
      });

      // Send email verification
      await sendEmailVerification(user);

      // Success - redirect to home page
      navigate('/');
      
    } catch (error) {
      console.error('Signup error:', error);
      
      // Handle specific Firebase errors
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError(t('errors.emailAlreadyInUse'));
          break;
        case 'auth/invalid-email':
          setError(t('errors.invalidEmail'));
          break;
        case 'auth/weak-password':
          setError(t('errors.passwordTooWeak'));
          break;
        default:
          setError(t('errors.accountCreationFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAgreementAccept = () => {
    setAgreementAccepted(true);
    setShowAgreement(false);
  };

  const handleAgreementDecline = () => {
    setAgreementAccepted(false);
    setShowAgreement(false);
  };

  const handleRecaptchaVerify = () => {
    setRecaptchaError('');
    // Clear any existing error when reCAPTCHA is verified
    if (error && error.includes('reCAPTCHA')) {
      setError('');
    }
  };

  const handleRecaptchaError = (error) => {
    setRecaptchaError(error);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      // Check if user document exists in Firestore
      const userDoc = await getUser(user.uid);
      
      // If user doesn't exist in Firestore, create a new user document
      if (!userDoc) {
        await createUser(user.uid, user.email, {
          displayName: user.displayName || null,
          emailVerified: user.emailVerified
        });
      }

      // Navigate to home page
      navigate('/');
    } catch (err) {
      console.error('Google sign-in error:', err);
      
      // Handle specific Firebase errors
      switch (err.code) {
        case 'auth/popup-closed-by-user':
          setError(t('errors.popupClosed'));
          break;
        case 'auth/popup-blocked':
          setError(t('errors.popupBlocked'));
          break;
        case 'auth/account-exists-with-different-credential':
          setError(t('errors.accountExistsDifferentCredential'));
          break;
        case 'auth/auth-domain-config-required':
          setError(t('errors.googleSignInNotConfigured'));
          break;
        default:
          setError(t('errors.googleSignInFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Create Your Osailu Account"
        description="Join Osailu today and start automating your virtual learning environments. Create a free account to access powerful educational automation tools."
        keywords="Osailu signup, create account, register Osailu, virtual learning platform registration"
        url="/signup"
        noindex={true}
      />
      <div style={{ 
        minHeight: 'calc(100vh - 120px)', 
        backgroundColor: 'var(--bg-color)',
        color: 'var(--text-color)',
        transition: 'background-color 0.3s ease, color 0.3s ease',
        padding: '20px 0'
      }}>
      <div className="container mt-5">
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card>
            <Card.Header className="text-center">
              <h3>{t('auth.createAccount')}</h3>
              <p className="text-muted mb-0">{t('auth.joinOsailu')}</p>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.fullName')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleInputChange}
                    placeholder={t('auth.enterFullName')}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.emailAddress')}</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('auth.enterEmail')}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.password')}</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={t('auth.createPassword')}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.confirmPassword')}</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder={t('auth.confirmPasswordPlaceholder')}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="agreement-check"
                    label={
                      <span>
                        {t('auth.agreeToTerms')}{' '}
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none"
                          onClick={() => setShowAgreement(true)}
                        >
                          {t('auth.userAgreement')}
                        </Button>
                      </span>
                    }
                    checked={agreementAccepted}
                    onChange={(e) => setAgreementAccepted(e.target.checked)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <RecaptchaComponent
                    ref={recaptchaRef}
                    onVerify={handleRecaptchaVerify}
                    onError={handleRecaptchaError}
                    siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  />
                  {recaptchaError && (
                    <div className="text-danger small mt-1">
                      {recaptchaError}
                    </div>
                  )}
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 mb-3"
                  disabled={loading}
                >
                  {loading ? t('auth.creatingAccount') : t('auth.createAccount')}
                </Button>
              </Form>

              <div className="text-center mb-3">
                <div className="position-relative">
                  <hr />
                  <span className="position-absolute top-50 start-50 translate-middle px-2 text-muted small" style={{ backgroundColor: 'var(--bg-color)' }}>
                    {t('common.or')}
                  </span>
                </div>
              </div>

              <Button
                variant="outline-primary"
                className="w-100 mb-3"
                onClick={handleGoogleSignIn}
                disabled={loading}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <g fill="#000" fillRule="evenodd">
                    <path d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z" fill="#EA4335"/>
                    <path d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.21 1.18-.84 2.18-1.79 2.91l2.84 2.2c1.7-1.57 2.68-3.88 2.68-6.61z" fill="#4285F4"/>
                    <path d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9.008 9.008 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z" fill="#FBBC05"/>
                    <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.84-2.2c-.76.53-1.78.9-3.12.9-2.38 0-4.4-1.57-5.12-3.74L.96 13.04C2.45 15.98 5.48 18 9 18z" fill="#34A853"/>
                  </g>
                </svg>
                {loading ? t('auth.signingIn') : t('auth.continueWithGoogle')}
              </Button>

              <div className="text-center">
                <p className="mb-0">
                  {t('auth.alreadyHaveAccount')}{' '}
                  <Link to="/login" className="text-decoration-none">
                    {t('auth.signInHere')}
                  </Link>
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <UserAgreementModal
        show={showAgreement}
        onAccept={handleAgreementAccept}
        onDecline={handleAgreementDecline}
      />
    </div>
    </div>
    </>
  );
};

const SignupWithRecaptcha = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  
  if (!recaptchaSiteKey) {
    console.warn('reCAPTCHA site key not configured');
    return <Signup />;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <Signup />
    </GoogleReCaptchaProvider>
  );
};

export default SignupWithRecaptcha;
